import { useState, useEffect, useRef, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../supabaseClient'
import { useAuth } from '../../context/AuthContext'
import { Bell, CheckCheck } from 'lucide-react'

const tipoLabel = {
  revisione_ricevuta:    '📋 Nuova revisione',
  commento_revisione:    '💬 Nuovo commento',
  revisione_approvata:   '✅ Revisione approvata',
  revisione_modifiche:   '🔄 Modifiche richieste',
  task_assegnata:        '📌 Task assegnata',
  milestone_raggiunta:   '🏁 Milestone raggiunta',
  pagamento_ricevuto:    '💰 Pagamento ricevuto',
}

const NotificheBell = () => {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [notifiche, setNotifiche] = useState([])
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState({ top: 0, right: 0 })
  const panelRef = useRef(null)
  const btnRef = useRef(null)
  const channelRef = useRef(null)
  const myId = profile?.id

  // Calcola la posizione del pannello rispetto al bottone (portal su body)
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return
    const update = () => {
      const r = btnRef.current.getBoundingClientRect()
      setCoords({ top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right) })
    }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [open])

  const fetchNotifiche = async () => {
    if (!myId) return
    const { data } = await supabase
      .from('notifiche')
      .select('*')
      .eq('user_id', myId)
      .order('created_at', { ascending: false })
      .limit(30)
    setNotifiche(data || [])
  }

  useEffect(() => {
    if (!myId) return
    fetchNotifiche()

    if (channelRef.current) supabase.removeChannel(channelRef.current)
    const ch = supabase.channel(`notifiche-${myId}`)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'notifiche',
        filter: `user_id=eq.${myId}`,
      }, () => fetchNotifiche())
      .subscribe()
    channelRef.current = ch
    return () => supabase.removeChannel(ch)
  }, [myId]) // eslint-disable-line react-hooks/exhaustive-deps

  /* chiudi cliccando fuori (bottone + pannello nel portal) */
  useEffect(() => {
    const handler = (e) => {
      if (btnRef.current?.contains(e.target)) return
      if (panelRef.current?.contains(e.target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const unread = notifiche.filter(n => !n.letta).length

  const markAllRead = async () => {
    if (!unread) return
    await supabase.from('notifiche').update({ letta: true }).eq('user_id', myId).eq('letta', false)
    setNotifiche(prev => prev.map(n => ({ ...n, letta: true })))
  }

  const handleClick = async (n) => {
    if (!n.letta) {
      await supabase.from('notifiche').update({ letta: true }).eq('id', n.id)
      setNotifiche(prev => prev.map(x => x.id === n.id ? { ...x, letta: true } : x))
    }
    setOpen(false)
    if (n.link) navigate(n.link)
  }

  const fmtTime = (iso) => {
    const d = new Date(iso)
    const now = new Date()
    const diff = Math.floor((now - d) / 1000)
    if (diff < 60) return 'Ora'
    if (diff < 3600) return `${Math.floor(diff / 60)}m fa`
    if (diff < 86400) return `${Math.floor(diff / 3600)}h fa`
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })
  }

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={() => setOpen(v => !v)}
        className="relative p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-0.5 shadow">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          style={{ position: 'fixed', top: coords.top, right: coords.right, width: 'min(20rem, calc(100vw - 16px))' }}
          className="bg-gray-900 border border-gray-800 rounded-2xl shadow-2xl z-[100] overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
            <span className="text-white font-semibold text-sm">Notifiche</span>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-violet-400 transition-colors"
              >
                <CheckCheck size={13} /> Segna tutte lette
              </button>
            )}
          </div>

          {/* Lista */}
          <div className="max-h-96 overflow-y-auto divide-y divide-gray-800/60">
            {notifiche.length === 0 ? (
              <div className="py-10 text-center">
                <Bell size={28} className="text-gray-700 mx-auto mb-2" />
                <p className="text-gray-600 text-sm">Nessuna notifica</p>
              </div>
            ) : notifiche.map(n => (
              <button
                key={n.id}
                onClick={() => handleClick(n)}
                className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-800/60 transition-colors ${!n.letta ? 'bg-violet-600/5' : ''}`}
              >
                {!n.letta && (
                  <span className="w-1.5 h-1.5 rounded-full bg-violet-500 flex-shrink-0 mt-2" />
                )}
                {n.letta && <span className="w-1.5 flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-violet-400 font-medium mb-0.5">
                    {tipoLabel[n.tipo] || n.tipo}
                  </p>
                  <p className={`text-sm truncate ${n.letta ? 'text-gray-400' : 'text-white font-medium'}`}>
                    {n.titolo}
                  </p>
                  {n.body && <p className="text-xs text-gray-500 truncate mt-0.5">{n.body}</p>}
                </div>
                <span className="text-[10px] text-gray-600 flex-shrink-0 mt-0.5">{fmtTime(n.created_at)}</span>
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default NotificheBell
